/* ── Document & Form Engine ─────────────────────────────────────── */

export type TemplateCategory = "Contract" | "Disclosure" | "Change Order" | "Form";
export type TemplateStatus = "Active" | "Inactive" | "Archived";

export interface TemplateVariable {
  key: string;
  label: string;
  source: "quote" | "job" | "customer" | "system" | "financial";
  example: string;
}

/** All supported dynamic variables */
export const TEMPLATE_VARIABLES: TemplateVariable[] = [
  // Customer
  { key: "customer_name", label: "Customer Name", source: "customer", example: "John & Jane Smith" },
  { key: "secondary_customer_name", label: "Secondary Customer Name", source: "customer", example: "Jane Smith" },
  { key: "address", label: "Address", source: "customer", example: "4521 Bay Vista Dr, Tampa, FL 33611" },
  { key: "phone", label: "Phone", source: "customer", example: "(813) 555-0142" },
  { key: "email", label: "Email", source: "customer", example: "john.smith@email.com" },
  // Project
  { key: "project_type", label: "Project Type", source: "quote", example: "Tub-to-Shower Conversion" },
  { key: "selected_materials", label: "Selected Materials", source: "quote", example: "Acrylic Wall System – White Marble" },
  { key: "selected_system", label: "Selected System", source: "quote", example: "Walk-in Shower" },
  { key: "selected_dimensions", label: "Dimensions", source: "quote", example: '60" × 32"' },
  { key: "selected_accessories", label: "Selected Accessories", source: "quote", example: "Corner Shelf, Grab Bar, Soap Dish" },
  // Financial
  { key: "price", label: "Total Price", source: "financial", example: "$12,450.00" },
  { key: "deposit_amount", label: "Deposit Amount", source: "financial", example: "$6,225.00" },
  { key: "balance_due", label: "Balance Due", source: "financial", example: "$6,225.00" },
  { key: "payment_terms", label: "Payment Terms", source: "financial", example: "50% deposit, balance due at completion" },
  { key: "payment_method", label: "Payment Method", source: "financial", example: "Cash" },
  // System
  { key: "sales_rep_name", label: "Sales Rep Name", source: "system", example: "Angela Wright" },
  { key: "date", label: "Current Date", source: "system", example: "March 27, 2026" },
  { key: "contract_number", label: "Contract Number", source: "system", example: "CCC-2026-0342" },
  { key: "job_id", label: "Job ID", source: "system", example: "JOB-042" },
  { key: "quote_id", label: "Quote ID", source: "system", example: "Q-2026-015" },
  { key: "company_name", label: "Company Name", source: "system", example: "Creative Closets & Conversions" },
  { key: "company_phone", label: "Company Phone", source: "system", example: "(813) 555-0100" },
  { key: "company_address", label: "Company Address", source: "system", example: "4200 W Kennedy Blvd, Tampa, FL 33609" },
  { key: "rescission_end_date", label: "Rescission End Date", source: "system", example: "March 30, 2026" },
];

export interface TemplateSection {
  id: string;
  title: string;
  type: "header" | "body" | "terms" | "signature";
  content: string;
  order: number;
}

export interface TemplateVersion {
  version: number;
  sections: TemplateSection[];
  createdAt: string;
  createdBy: string;
  changeNotes: string;
}

export interface DocumentTemplate {
  id: string;
  name: string;
  category: TemplateCategory;
  status: TemplateStatus;
  currentVersion: number;
  versions: TemplateVersion[];
  createdAt: string;
  createdBy: string;
  lastUpdatedAt: string;
  lastUpdatedBy: string;
}

export interface GeneratedDocument {
  id: string;
  templateId: string;
  templateName: string;
  templateVersion: number;
  category: TemplateCategory;
  /** Rendered sections with variables resolved */
  renderedSections: TemplateSection[];
  /** Snapshot of variable values used */
  variableSnapshot: Record<string, string>;
  /** Source context */
  sourceType: "quote" | "job" | "change_order";
  sourceId: string;
  /** Signatures */
  customerSignature?: string;
  customerSignedAt?: string;
  repSignature?: string;
  repSignedAt?: string;
  /** Status */
  status: "Draft" | "Sent" | "Signed" | "Cancelled";
  locked: boolean;
  /** Tracking */
  generatedAt: string;
  generatedBy: string;
}

/* ── Variable Resolution ──────────────────────────────────────── */

export function resolveVariables(content: string, values: Record<string, string>): string {
  return content.replace(/\{\{(\w+)\}\}/g, (match, key) => {
    return values[key] ?? match;
  });
}

export function extractVariables(content: string): string[] {
  const matches = content.match(/\{\{(\w+)\}\}/g) || [];
  return [...new Set(matches.map(m => m.replace(/\{\{|\}\}/g, "")))];
}

export function validateTemplate(sections: TemplateSection[]): { valid: boolean; issues: string[] } {
  const issues: string[] = [];
  const allContent = sections.map(s => s.content).join("\n");
  const usedVars = extractVariables(allContent);
  const knownKeys = new Set(TEMPLATE_VARIABLES.map(v => v.key));

  for (const v of usedVars) {
    if (!knownKeys.has(v)) {
      issues.push(`Unknown variable: {{${v}}}`);
    }
  }

  if (!sections.some(s => s.type === "header")) issues.push("Missing header section");
  if (!sections.some(s => s.type === "body")) issues.push("Missing body section");

  return { valid: issues.length === 0, issues };
}

/* ── Default section helpers ──────────────────────────────────── */

function defaultSections(category: TemplateCategory): TemplateSection[] {
  const base: TemplateSection[] = [
    { id: "s1", title: "Header", type: "header", content: `{{company_name}}\n{{company_address}} · {{company_phone}}\n\n${category.toUpperCase()}\nDate: {{date}}\nContract #: {{contract_number}}`, order: 1 },
  ];

  if (category === "Contract") {
    base.push(
      { id: "s2", title: "Customer Information", type: "body", content: "Customer: {{customer_name}}\nAddress: {{address}}\nPhone: {{phone}} · Email: {{email}}\n\nProject: {{project_type}}\nSystem: {{selected_system}}\nDimensions: {{selected_dimensions}}\nMaterials: {{selected_materials}}\nAccessories: {{selected_accessories}}", order: 2 },
      { id: "s3", title: "Pricing & Payment", type: "body", content: "Total Contract Price: {{price}}\nDeposit: {{deposit_amount}}\nBalance Due at Completion: {{balance_due}}\nPayment Method: {{payment_method}}\nTerms: {{payment_terms}}", order: 3 },
      { id: "s4", title: "Terms & Conditions", type: "terms", content: "1. Work will be completed within the estimated timeframe.\n2. Customer has the right to cancel within 3 business days (see Rescission Notice).\n3. Final payment is due upon satisfactory completion of work.\n4. Warranty: Lifetime limited warranty on materials; 1-year labor warranty.\n5. Any changes to the scope of work require a signed Change Order.", order: 4 },
      { id: "s5", title: "Signatures", type: "signature", content: "By signing below, both parties agree to the terms and conditions outlined in this contract.\n\nHomeowner Signature: ______________________ Date: ______\nSales Representative: {{sales_rep_name}} Date: {{date}}", order: 5 },
    );
  } else if (category === "Disclosure") {
    base.push(
      { id: "s2", title: "Notice Content", type: "body", content: "NOTICE OF RIGHT TO CANCEL\n\nYou, {{customer_name}}, are entering into a transaction on {{date}} at {{address}}.\n\nYou may cancel this transaction without penalty or obligation within THREE (3) BUSINESS DAYS from the above date.\n\nRescission period ends: {{rescission_end_date}}", order: 2 },
      { id: "s3", title: "Acknowledgment", type: "signature", content: "I acknowledge receipt of this notice and understand my right to cancel.\n\nCustomer Signature: ______________________ Date: ______", order: 3 },
    );
  } else if (category === "Change Order") {
    base.push(
      { id: "s2", title: "Change Details", type: "body", content: "Original Contract: {{contract_number}}\nCustomer: {{customer_name}}\nJob: {{job_id}}\n\nScope Changes:\n[Change items will be auto-populated]\n\nRevised Total: {{price}}", order: 2 },
      { id: "s3", title: "Approval", type: "signature", content: "Both parties agree to the changes described above.\n\nHomeowner: ______________________ Date: ______\nRepresentative: {{sales_rep_name}} Date: {{date}}", order: 3 },
    );
  } else {
    base.push(
      { id: "s2", title: "Form Content", type: "body", content: "Job: {{job_id}}\nCustomer: {{customer_name}}\nAddress: {{address}}\n\n[Form fields will be defined here]", order: 2 },
    );
  }

  return base;
}

/* ── Seed Templates ───────────────────────────────────────────── */

export const SEED_TEMPLATES: DocumentTemplate[] = [
  {
    id: "tpl-1",
    name: "Standard Bathroom Remodel Contract",
    category: "Contract",
    status: "Active",
    currentVersion: 3,
    versions: [
      { version: 1, sections: defaultSections("Contract"), createdAt: "2026-01-15", createdBy: "Mike Reynolds", changeNotes: "Initial contract template" },
      { version: 2, sections: defaultSections("Contract"), createdAt: "2026-02-20", createdBy: "Sarah Chen", changeNotes: "Added warranty section" },
      { version: 3, sections: defaultSections("Contract"), createdAt: "2026-03-20", createdBy: "Mike Reynolds", changeNotes: "Updated payment terms language" },
    ],
    createdAt: "2026-01-15",
    createdBy: "Mike Reynolds",
    lastUpdatedAt: "2026-03-20",
    lastUpdatedBy: "Mike Reynolds",
  },
  {
    id: "tpl-2",
    name: "3-Day Right of Rescission Notice",
    category: "Disclosure",
    status: "Active",
    currentVersion: 1,
    versions: [
      { version: 1, sections: defaultSections("Disclosure"), createdAt: "2026-03-15", createdBy: "Sarah Chen", changeNotes: "Standard rescission notice" },
    ],
    createdAt: "2026-03-15",
    createdBy: "Sarah Chen",
    lastUpdatedAt: "2026-03-15",
    lastUpdatedBy: "Sarah Chen",
  },
  {
    id: "tpl-3",
    name: "HOA Approval Form",
    category: "Form",
    status: "Active",
    currentVersion: 1,
    versions: [
      { version: 1, sections: defaultSections("Form"), createdAt: "2026-03-18", createdBy: "James Porter", changeNotes: "Initial HOA form" },
    ],
    createdAt: "2026-03-18",
    createdBy: "James Porter",
    lastUpdatedAt: "2026-03-18",
    lastUpdatedBy: "James Porter",
  },
  {
    id: "tpl-4",
    name: "POA Authorization Form",
    category: "Form",
    status: "Active",
    currentVersion: 2,
    versions: [
      { version: 1, sections: defaultSections("Form"), createdAt: "2026-02-10", createdBy: "Mike Reynolds", changeNotes: "Initial POA form" },
      { version: 2, sections: defaultSections("Form"), createdAt: "2026-03-10", createdBy: "Mike Reynolds", changeNotes: "Added notarization section" },
    ],
    createdAt: "2026-02-10",
    createdBy: "Mike Reynolds",
    lastUpdatedAt: "2026-03-10",
    lastUpdatedBy: "Mike Reynolds",
  },
  {
    id: "tpl-5",
    name: "Change Order Agreement",
    category: "Change Order",
    status: "Active",
    currentVersion: 1,
    versions: [
      { version: 1, sections: defaultSections("Change Order"), createdAt: "2026-03-12", createdBy: "Sarah Chen", changeNotes: "Standard change order template" },
    ],
    createdAt: "2026-03-12",
    createdBy: "Sarah Chen",
    lastUpdatedAt: "2026-03-12",
    lastUpdatedBy: "Sarah Chen",
  },
  {
    id: "tpl-6",
    name: "QA Inspection Checklist",
    category: "Form",
    status: "Active",
    currentVersion: 1,
    versions: [
      { version: 1, sections: [
        { id: "s1", title: "Header", type: "header", content: "QA INSPECTION REPORT\nJob: {{job_id}} · Date: {{date}}\nCustomer: {{customer_name}} · Address: {{address}}", order: 1 },
        { id: "s2", title: "Checklist", type: "body", content: "[ ] Site clean and debris removed\n[ ] All fixtures properly installed\n[ ] Plumbing connections verified\n[ ] No visible damage or defects\n[ ] Customer walkthrough completed\n[ ] Final photos captured", order: 2 },
        { id: "s3", title: "Sign-Off", type: "signature", content: "Inspector: ______________________ Date: ______\nCustomer Acceptance: ______________________ Date: ______", order: 3 },
      ], createdAt: "2026-03-22", createdBy: "Sarah Chen", changeNotes: "Standard QA form" },
    ],
    createdAt: "2026-03-22",
    createdBy: "Sarah Chen",
    lastUpdatedAt: "2026-03-22",
    lastUpdatedBy: "Sarah Chen",
  },
];

/** Get current version's sections */
export function getCurrentSections(template: DocumentTemplate): TemplateSection[] {
  const ver = template.versions.find(v => v.version === template.currentVersion);
  return ver?.sections || [];
}

/** Generate example values for preview */
export function getExampleValues(): Record<string, string> {
  const values: Record<string, string> = {};
  for (const v of TEMPLATE_VARIABLES) {
    values[v.key] = v.example;
  }
  return values;
}
